import { useCallback, useEffect, useMemo, useState } from "react";
import { Show, useAuth } from "@clerk/react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Shield } from "lucide-react";
import Nav from "@/components/Nav";
import { makeApi } from "@/lib/api";
import { useBranding, refreshBranding } from "@/lib/branding";
import { MASCOT_URL } from "@/components/Mascot";
import AdminThemes from "@/components/admin/AdminThemes";
import AdminPublishRequests from "@/components/admin/AdminPublishRequests";
import AdminBackups from "@/components/admin/AdminBackups";
import AdminQuestionPanel from "@/components/admin/AdminQuestionPanel";

const STATUS_PILL = (s) => `px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${
  s === "approved" ? "bg-[var(--tp-success)] text-white" : s === "rejected" ? "bg-[#FF4B4B] text-white" : "bg-[var(--tp-accent)]"}`;
const SMALL_BTN = "px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none";

function AdminTeachers({ api }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/teacher-requests").then((r) => setRows(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, a) => { await api.post(`/admin/teacher-requests/${id}/${a}`); toast.success(a === "approve" ? "Teacher verified" : "Rejected"); load(); };
  return (
    <div className="space-y-3" data-testid="admin-teacher-requests">
      {rows.length === 0 && <p className="text-slate-500">No teacher requests yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="sticker-card p-5 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-black">{r.name || r.email} <span className="text-slate-500 font-bold text-sm">{r.email}</span></p>
            <p className="text-sm"><b>School:</b> {r.school}</p>
            {r.message && <p className="text-sm text-slate-600">{r.message}</p>}
          </div>
          <span className={STATUS_PILL(r.status)}>{r.status}</span>
          {r.status === "pending" && (<>
            <button onClick={() => act(r.id, "approve")} data-testid={`approve-teacher-${r.id}`} className={`${SMALL_BTN} bg-[var(--tp-success)] text-white`}>Verify</button>
            <button onClick={() => act(r.id, "reject")} data-testid={`reject-teacher-${r.id}`} className={`${SMALL_BTN} bg-white`}>Reject</button>
          </>)}
        </div>
      ))}
    </div>
  );
}

function AdminClassrooms({ api }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/classrooms").then((r) => setRows(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, a) => { await api.post(`/admin/classrooms/${id}/${a}`); load(); };
  return (
    <div className="space-y-3" data-testid="admin-classrooms">
      {rows.length === 0 && <p className="text-slate-500">No classrooms yet.</p>}
      {rows.map((c) => (
        <div key={c.id} className="sticker-card p-5 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-black">{c.name} <span className="text-slate-400 font-bold text-sm">code {c.code}</span></p>
            <p className="text-sm text-slate-600">Teacher: {c.teacher_name || c.teacher_id} • {c.member_count} students</p>
          </div>
          <span className={STATUS_PILL(c.status)}>{c.status}</span>
          {c.status !== "approved" && <button onClick={() => act(c.id, "approve")} data-testid={`approve-classroom-${c.id}`} className={`${SMALL_BTN} bg-[var(--tp-success)] text-white`}>Approve</button>}
          {c.status !== "rejected" && <button onClick={() => act(c.id, "reject")} data-testid={`reject-classroom-${c.id}`} className={`${SMALL_BTN} bg-white`}>Reject</button>}
        </div>
      ))}
    </div>
  );
}

function BrandingSlot({ api, kind, label, hint, current }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/branding/${kind}`, fd); await refreshBranding(); toast.success(`${label} updated`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const reset = async () => { await api.delete(`/admin/branding/${kind}`); await refreshBranding(); toast.success("Reset to default"); };
  return (
    <div className="sticker-card p-6">
      <h3 className="font-black text-lg">{label}</h3>
      <p className="text-sm text-slate-600">{hint}</p>
      <div className="mt-4 w-28 h-28 rounded-full border-[3px] border-[#374151] overflow-hidden bg-[var(--tp-accent)] flex items-center justify-center font-black text-3xl">
        {current || kind === "mascot" ? <img src={current || MASCOT_URL} alt={label} className="w-full h-full object-cover" data-testid={`branding-preview-${kind}`} /> : "TP"}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <label className={`${SMALL_BTN} bg-[var(--tp-primary)] text-white cursor-pointer`}>
          {busy ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" data-testid={`branding-upload-${kind}`} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {current && <button onClick={reset} className={`${SMALL_BTN} bg-white`} data-testid={`branding-reset-${kind}`}>Use default</button>}
      </div>
    </div>
  );
}

function AdminBranding({ api }) {
  const { icon_url, base_mascot_url } = useBranding();
  return (
    <div>
      <p className="text-slate-600 mb-4 font-bold">Swap these any time for seasonal looks (holiday hats, summer shades…). Changes show for everyone instantly.</p>
      <div className="grid md:grid-cols-2 gap-6" data-testid="admin-branding">
        <BrandingSlot api={api} kind="icon" label="App icon" hint="Used in the top bar and browser tab. Square image works best." current={icon_url} />
        <BrandingSlot api={api} kind="mascot" label="Mascot / thumbnail" hint="The big TP character on the home, sign-in and results screens (when a seasonal theme is active, its mascot shows instead)." current={base_mascot_url} />
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none ${active ? "bg-[var(--tp-accent)]" : "bg-white"}`}>
      {label}
    </button>
  );
}

export default function Admin() {
  const { getToken, isSignedIn } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("categories");

  useEffect(() => { if (isSignedIn) api.get("/me").then((r) => setMe(r.data)); }, [isSignedIn, api]);

  if (!isSignedIn) return <><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>;
  if (me && me.role !== "admin") {
    return <><Nav /><main className="max-w-xl mx-auto p-10 text-center">
      <Shield size={48} strokeWidth={2.5} className="mx-auto text-[#FF4B4B]" />
      <h1 className="mt-4 text-3xl font-black">Admin only</h1>
      <p className="text-slate-600 mt-2">Ask your admin to grant you access.</p>
    </main></>;
  }

  return (
    <>
      <Nav />
      <main className="max-w-6xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-black tracking-tight flex items-center gap-2">
          <Shield size={28} strokeWidth={2.5} /> Admin
        </h1>
        <div className="mt-6 flex gap-2 flex-wrap">
          <TabButton active={tab === "categories"} onClick={() => setTab("categories")} label="Categories" testid="tab-categories" />
          <TabButton active={tab === "quizzes"} onClick={() => setTab("quizzes")} label="Quizzes & Questions" testid="tab-quizzes" />
          <TabButton active={tab === "submissions"} onClick={() => setTab("submissions")} label="Submissions" testid="tab-submissions" />
          <TabButton active={tab === "users"} onClick={() => setTab("users")} label="Users" testid="tab-users" />
          <TabButton active={tab === "teachers"} onClick={() => setTab("teachers")} label="Teacher requests" testid="tab-teachers" />
          <TabButton active={tab === "classrooms"} onClick={() => setTab("classrooms")} label="Classrooms" testid="tab-classrooms" />
          <TabButton active={tab === "branding"} onClick={() => setTab("branding")} label="App icon & mascot" testid="tab-branding" />
          <TabButton active={tab === "themes"} onClick={() => setTab("themes")} label="Seasonal themes" testid="tab-themes" />
          <TabButton active={tab === "publish"} onClick={() => setTab("publish")} label="Publish requests" testid="tab-publish" />
          <TabButton active={tab === "backups"} onClick={() => setTab("backups")} label="Backups" testid="tab-backups" />
        </div>

        <div className="mt-8">
          {tab === "backups" && <AdminBackups api={api} />}
          {tab === "themes" && <AdminThemes api={api} />}
          {tab === "publish" && <AdminPublishRequests api={api} />}
          {tab === "teachers" && <AdminTeachers api={api} />}
          {tab === "classrooms" && <AdminClassrooms api={api} />}
          {tab === "branding" && <AdminBranding api={api} />}
          {tab === "categories" && <AdminCategories api={api} />}
          {tab === "quizzes" && <AdminQuizzes api={api} />}
          {tab === "submissions" && <AdminSubmissions api={api} />}
          {tab === "users" && <AdminUsers api={api} />}
        </div>
      </main>
    </>
  );
}

function AdminCategories({ api }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image: "", is_free: true });
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name || !form.slug) return toast.error("Name & slug required");
    try { await api.post("/admin/categories", form); toast.success("Category created"); setForm({ name: "", slug: "", description: "", image: "", is_free: true }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/categories/${id}`); load(); };

  return (
    <div>
      <div className="sticker-card p-5">
        <h3 className="font-black text-lg mb-3">New category</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name"
            className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold" />
          <input placeholder="Slug (e.g. math)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="cat-slug"
            className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold" />
          <input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold sm:col-span-2" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold sm:col-span-2" />
        </div>
        <button onClick={save} data-testid="cat-save"
          className="mt-4 px-5 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none inline-flex items-center gap-1">
          <Plus size={16} strokeWidth={2.5} /> Create
        </button>
      </div>
      <div className="sticker mt-6" data-testid="categories-admin-list">
        {cats.map((c, i) => (
          <div key={c.id} className={`flex items-center gap-4 p-4 ${i !== cats.length - 1 ? "border-b-[2px] border-slate-200" : ""}`}>
            <div className="flex-1 min-w-0">
              <p className="font-black">{c.name} <span className="text-slate-400 text-sm">/{c.slug}</span></p>
              <p className="text-sm text-slate-500 truncate">{c.description}</p>
            </div>
            <button onClick={() => del(c.id)} className="text-[#FF4B4B]"><Trash2 size={18} strokeWidth={2.5} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminQuizzes({ api }) {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [quizzes, setQuizzes] = useState([]);
  const [quizForm, setQuizForm] = useState({ title: "", description: "", is_free: true });
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/categories").then((r) => { setCats(r.data); setCatId((cur) => cur || r.data[0]?.id || ""); }); }, [api]);
  useEffect(() => { if (catId) api.get(`/categories/${catId}/quizzes`).then((r) => setQuizzes(r.data)); }, [catId, api]);
  const loadQuiz = async (q) => {
    const r = await api.get(`/quizzes/${q.id}`); setSelected(r.data);
  };
  const createQuiz = async () => {
    if (!quizForm.title) return toast.error("Title required");
    await api.post("/admin/quizzes", { ...quizForm, category_id: catId });
    toast.success("Quiz created"); setQuizForm({ title: "", description: "", is_free: true });
    const r = await api.get(`/categories/${catId}/quizzes`); setQuizzes(r.data);
  };
  const delQuiz = async (id) => { if (!window.confirm("Delete quiz & its questions?")) return; await api.delete(`/admin/quizzes/${id}`); setSelected(null);
    const r = await api.get(`/categories/${catId}/quizzes`); setQuizzes(r.data); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <label className="font-black text-sm">Category</label>
        <select value={catId} onChange={(e) => { setCatId(e.target.value); setSelected(null); }} data-testid="admin-cat-select"
          className="mt-1 w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold bg-white">
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <div className="sticker-card p-5 mt-4">
          <h3 className="font-black mb-3">New quiz</h3>
          <input placeholder="Title" value={quizForm.title} onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })} data-testid="quiz-title"
            className="w-full px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold" />
          <input placeholder="Description" value={quizForm.description} onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
            className="mt-2 w-full px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold" />
          <button onClick={createQuiz} data-testid="quiz-create"
            className="mt-3 px-5 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
            Create quiz
          </button>
        </div>

        <div className="sticker mt-6">
          {quizzes.map((q, i) => (
            <div key={q.id} className={`flex items-center gap-3 p-3 ${i !== quizzes.length - 1 ? "border-b-[2px] border-slate-200" : ""}`}>
              <button onClick={() => loadQuiz(q)} data-testid={`admin-quiz-${q.id}`} className="flex-1 text-left font-black truncate hover:underline">
                {q.title}
              </button>
              <button onClick={() => delQuiz(q.id)} className="text-[#FF4B4B]"><Trash2 size={16} strokeWidth={2.5} /></button>
            </div>
          ))}
          {quizzes.length === 0 && <p className="p-4 text-slate-500">No quizzes yet.</p>}
        </div>
      </div>

      <div>
        {!selected && <p className="text-slate-500">Pick a quiz on the left to manage questions.</p>}
        {selected && (
          <AdminQuestionPanel key={selected.id} api={api} quiz={selected} />
        )}
      </div>
    </div>
  );
}

function AdminSubmissions({ api }) {
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const load = useCallback(() => api.get("/admin/submissions").then((r) => setSubs(r.data)), [api]);
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, [load, api]);
  const setCat = async (sid, cid) => { await api.patch(`/admin/submissions/${sid}/category`, { category_id: cid }); load(); };
  const approve = async (sid) => { try { await api.post(`/admin/submissions/${sid}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const reject = async (sid) => { await api.post(`/admin/submissions/${sid}/reject`); load(); };

  return (
    <div className="space-y-4" data-testid="admin-submissions-list">
      {subs.length === 0 && <p className="text-slate-500">No submissions yet.</p>}
      {subs.map((s) => (
        <div key={s.id} className="sticker-card p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-black text-lg">{s.quiz_title}</p>
              <p className="text-sm text-slate-500">from {s.user_name || s.user_email} • {s.questions.length} question(s)</p>
              {s.proposed_category && <p className="text-sm">Proposed category: <b>{s.proposed_category}</b></p>}
            </div>
            <span className={`px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${
              s.status === "approved" ? "bg-[var(--tp-success)] text-white" :
              s.status === "rejected" ? "bg-[#FF4B4B] text-white" : "bg-[var(--tp-accent)]"
            }`}>{s.status}</span>
          </div>
          <div className="mt-4 space-y-1 text-sm">
            {s.questions.slice(0, 3).map((q, i) => (
              <p key={i} className="text-slate-700"><b>{i + 1}.</b> {q.text}</p>
            ))}
            {s.questions.length > 3 && <p className="text-slate-500">+ {s.questions.length - 3} more…</p>}
          </div>
          {s.status === "pending" && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <select value={s.category_id || ""} onChange={(e) => setCat(s.id, e.target.value)}
                className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold bg-white">
                <option value="">Choose category…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={() => approve(s.id)} data-testid={`approve-${s.id}`}
                className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
                Approve & publish
              </button>
              <button onClick={() => reject(s.id)} data-testid={`reject-${s.id}`}
                className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
                Reject
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AdminUsers({ api }) {
  const [data, setData] = useState({ roles: [], users: [] });
  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const setRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="sticker" data-testid="admin-users-list">
      {data.users.map((u, i) => (
        <div key={u.user_id} className={`flex items-center gap-3 p-4 ${i !== data.users.length - 1 ? "border-b-[2px] border-slate-200" : ""}`}>
          <div className="flex-1 min-w-0">
            <p className="font-black truncate">{u.name || u.email || u.user_id}</p>
            <p className="text-sm text-slate-500 truncate">{u.email}</p>
          </div>
          <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)}
            className="px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold bg-white">
            {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      ))}
      {data.users.length === 0 && <p className="p-5 text-slate-500">No users yet.</p>}
    </div>
  );
}
